import { Material } from '@/app/entities/Material'
import { withoutCdn } from '@/app/utils/withCdn'
import { MaterialFormValues } from '@/view/pages/Conteudos/hooks/useMaterialForm'
import { parseFormValuesToMaterialFormData } from '@/view/pages/Conteudos/MaterialPage/helper'
import { httpClient } from '../http/httpClient'

const API_URL = '/materials'

export interface MaterialsPaginate {
  materials: Material[]
  limit: number
  skip: number
  sort: string
}

interface UploadMaterialContentPayload {
  feedImages: File[]
  storyImages: File[]
  youtubeImages: File[]
}

interface getMaterialsParams {
  search: string
  limit?: number
  skip?: number
  sort?: string
  startDate?: string
  endDate?: string
}

export class MaterialService {
  static async getMaterials(
    params: getMaterialsParams,
  ): Promise<MaterialsPaginate> {
    const { data } = await httpClient.get<MaterialsPaginate>(API_URL, {
      params,
    })
    return data
  }

  static async createMaterial(payload: MaterialFormValues) {
    const formData = parseFormValuesToMaterialFormData(payload)
    const { data } = await httpClient.post<Material>(API_URL, formData)

    const createdMaterialId = data._id

    await this.uploadMaterialContent(createdMaterialId, {
      feedImages: payload.feedImagesFiles,
      storyImages: payload.storyImagesFiles,
      youtubeImages: payload.youtubeImagesFiles,
    })

    return data
  }

  static async updateMaterial(
    materialId: string,
    payload: MaterialFormValues,
    oldMaterial: Material,
  ): Promise<Material> {
    const formData = parseFormValuesToMaterialFormData(payload)

    const { data } = await httpClient.put<Material>(
      `${API_URL}/${materialId}`,
      formData,
    )

    const createdMaterialId = data._id

    const newFeedImages = this.filterNewImages(
      payload.feedImagesFiles,
      oldMaterial.feedImages,
    )
    const newStoryImages = this.filterNewImages(
      payload.storyImagesFiles,
      oldMaterial.storyImages,
    )
    const newYoutubeImages = this.filterNewImages(
      payload.youtubeImagesFiles,
      oldMaterial.youtubeImages,
    )

    if (
      newFeedImages.length > 0 ||
      newStoryImages.length > 0 ||
      newYoutubeImages.length > 0
    ) {
      await this.uploadMaterialContent(createdMaterialId, {
        feedImages: newFeedImages,
        storyImages: newStoryImages,
        youtubeImages: newYoutubeImages,
      })
    }

    return data
  }

  static async deleteMaterial(materialId: string): Promise<boolean> {
    const { data } = await httpClient.delete<boolean>(
      `${API_URL}/${materialId}`,
    )
    return data
  }

  static async deleteMaterialContent(
    materialId: string,
    path: string,
  ): Promise<boolean> {
    const url = withoutCdn(path)

    const { data } = await httpClient.delete(
      `${API_URL}/content/${materialId}`,
      {
        data: { paths: [url] },
      },
    )

    return data
  }

  static async uploadMaterialContent(
    materialId: string,
    payload: UploadMaterialContentPayload,
  ): Promise<boolean> {
    const formData = new FormData()

    payload.feedImages.forEach((file) => {
      formData.append('feedImages', file)
    })
    payload.storyImages.forEach((file) => {
      formData.append('storyImages', file)
    })
    payload.youtubeImages.forEach((file) => {
      formData.append('youtubeImages', file)
    })

    const { data } = await httpClient.put(
      `${API_URL}/content/${materialId}`,
      formData,
    )

    return data
  }

  private static filterNewImages(newFiles: File[], oldFiles: string[]): File[] {
    return newFiles.filter((file) => !oldFiles.includes(file.name))
  }
}
